// Mapping decoded FIT activity files onto Khronos sessions (#77).
//
// This is the Garmin route that actually works. Their Connect Developer
// Program is enterprise-only and, as of 2026, paused; Strava's API agreement
// forbids feeding its data into an LLM context window, which is exactly what
// this history is for. A FIT file exported from your own Garmin account is
// subject to neither: it is your data, and it carries more than either API
// would give you anyway — `set` messages hold per-set reps and weight for
// strength work, which is the detail this app cares about most.
//
// Deliberately PURE. The Garmin SDK is ESM-only and Jest cannot transform it,
// so nothing here imports it: the decoded messages and the SDK's exercise-name
// tables are passed in as plain data. That keeps the whole mapping — the part
// with the judgement calls in it — unit-testable. See apis/fitDecoder.js for
// the thin adapter that produces them.

import { buildLoggedSessionFromPlan, buildLoggedSession } from "./loggedSession";
import { genStableId } from "./planEditing";

// --- Archive paths (Garmin bulk export) ---
//
// A single exported activity is a bare .fit file, but the full account export
// is a ZIP of ZIPs: the activities live inside
// DI_CONNECT/DI-Connect-Uploaded-Files/UploadedFiles_*.zip, alongside a great
// deal of JSON that is not ours to read. Classifying paths is the part with the
// edge cases in it, so it lives here where it can be tested.

const IGNORED_PATH_RE = /(^|\/)(__MACOSX\/|\.)/;

/** A directory entry, macOS resource fork, or dotfile — never a payload. */
export const isIgnoredArchivePath = (path) =>
  !path || path.endsWith("/") || IGNORED_PATH_RE.test(path);

const hasExtension = (path, ext) =>
  String(path || "")
    .toLowerCase()
    .endsWith(ext);

export const isFitPath = (path) =>
  !isIgnoredArchivePath(path) && hasExtension(path, ".fit");

export const isZipPath = (path) =>
  !isIgnoredArchivePath(path) && hasExtension(path, ".zip");

/**
 * What to do with each entry of an archive: decode it, descend into it, or
 * leave it alone. Returned as a plain classification so the walker in
 * apis/fitArchive.js stays a loop with no decisions in it.
 */
export const classifyArchiveEntry = (path) => {
  if (isFitPath(path)) return "fit";
  if (isZipPath(path)) return "archive";
  return "skip";
};

// FIT's `sport` enum onto the activity kinds a logged session can be. Anything
// unlisted lands on "other" rather than being guessed at.
const SPORT_TO_ACTIVITY = {
  running: "run",
  cycling: "ride",
  rowing: "row",
  swimming: "swim",
  walking: "walk",
  hiking: "walk",
  training: "strength",
  fitnessEquipment: "strength",
  transition: "other",
};

// A `set` message that records rest between working sets, not work itself.
const REST_SET_TYPE = "rest";

/**
 * "barbellBenchPress" -> "Barbell Bench Press".
 *
 * FIT enum names are lowerCamelCase; splitting on the case boundary and
 * title-casing gives back something readable. Digits stay attached to the word
 * they qualify ("90DegreeStaticHold" reads better than "90 Degree").
 */
export const humaniseExerciseName = (name) => {
  const raw = String(name || "").trim();
  if (!raw) return "";
  return raw
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase())
    .replace(/\s+/g, " ")
    .trim();
};

/**
 * Resolve one `set` message to a display label.
 *
 * FIT names an exercise in two parts: a `category` enum ("benchPress") and a
 * `categorySubtype` whose meaning depends on that category — subtype 1 of
 * benchPress is a barbell bench press, subtype 1 of squat is something else
 * entirely. `exerciseNames` is the SDK's per-category lookup, passed in so this
 * module stays free of the ESM-only SDK.
 *
 * Falls back to the category alone when the subtype is unknown, and to null
 * when even that is missing — an unnamed set is still a set, but it is the
 * caller's job to decide what to do with one.
 */
export const resolveExerciseLabel = (setMesg, exerciseNames = {}) => {
  const first = (v) => (Array.isArray(v) ? v[0] : v);
  const category = first(setMesg?.category);
  if (category == null) return null;

  const subtype = first(setMesg?.categorySubtype);
  const table = exerciseNames[`${category}ExerciseName`];
  const specific = table?.[subtype];

  return humaniseExerciseName(specific || category) || null;
};

const toFiniteNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Collapse a flat run of `set` messages into the plan tree the app stores.
 *
 * Consecutive sets of the SAME exercise become one sets-Group — that is the
 * compact shape docs/schema.md prescribes, and the one the load editors and
 * copied summary already read. When the reps or the weight changed across those
 * sets, the variation is preserved as a `setScheme` rather than flattened to
 * the first set's figures: an 8/6/5/5 at 60/65/70/70 IS the session, and
 * recording it as "4 x 8 @ 60" would be a different workout.
 *
 * Non-consecutive repeats stay separate — coming back to the bench after squats
 * is a second block, and merging them would misreport the order of the session.
 */
export const setsToPlan = (setMesgs, exerciseNames = {}, label = "Workout") => {
  const working = (Array.isArray(setMesgs) ? setMesgs : []).filter(
    (m) => String(m?.setType || "").toLowerCase() !== REST_SET_TYPE,
  );
  if (working.length === 0) return null;

  // Group consecutive same-exercise sets.
  const blocks = [];
  for (const mesg of working) {
    const exercise = resolveExerciseLabel(mesg, exerciseNames) || "Exercise";
    const reps = toFiniteNumber(mesg?.repetitions);
    const weight = toFiniteNumber(mesg?.weight);
    const last = blocks[blocks.length - 1];
    if (last && last.label === exercise) last.sets.push({ reps, weight });
    else blocks.push({ label: exercise, sets: [{ reps, weight }] });
  }

  const children = blocks.map((block) => {
    const count = block.sets.length;
    const repsList = block.sets.map((s) => s.reps);
    const weightList = block.sets.map((s) => s.weight);

    const hasWeight = weightList.some((w) => w != null && w > 0);
    const varies = (list) =>
      list.length > 1 && list.some((v) => v !== list[0]);

    const work = {
      id: genStableId(),
      type: "Work",
      label: block.label,
      unit: "reps",
      // Static fields mirror set 1 — the schema rule every consumer relies on.
      value: repsList[0] ?? 0,
      ...(hasWeight && weightList[0] != null
        ? { weight: Math.round(weightList[0] * 10) / 10 }
        : {}),
    };

    const scheme = {};
    if (varies(repsList) && repsList.every((r) => r != null)) {
      scheme.value = repsList;
    }
    if (hasWeight && varies(weightList) && weightList.every((w) => w != null)) {
      scheme.weight = weightList.map((w) => Math.round(w * 10) / 10);
    }
    if (Object.keys(scheme).length > 0) work.setScheme = scheme;

    return {
      id: genStableId(),
      type: "Group",
      label: block.label,
      unit: "sets",
      value: count,
      children: [work],
    };
  });

  return { id: genStableId(), label, type: "Strength", children };
};

/**
 * A stable identity for one activity, so re-importing a file — or importing a
 * bulk export that overlaps a previous one — updates the row rather than
 * adding a duplicate.
 *
 * The session's own start time is the natural key: two activities cannot begin
 * at the same instant on one device, and unlike a file name it survives being
 * re-exported. Garmin's own activity id is not present in every FIT file.
 */
export const fitExternalId = (sessionMesg) => {
  const start = sessionMesg?.startTime ?? sessionMesg?.timestamp;
  const ms = new Date(start).getTime();
  return Number.isFinite(ms) ? `fit-${ms}` : null;
};

const sessionLabel = (sport, subSport) => {
  const name = humaniseExerciseName(subSport) || humaniseExerciseName(sport);
  return name && name.toLowerCase() !== "generic" ? name : "Workout";
};

/**
 * Turn one decoded FIT session (plus the `set` messages that fall inside it)
 * into a candidate for import.
 *
 * Returns `{ payload, summary }` — the payload is exactly what the manual form
 * produces, so it travels the same POST, dedupe and history path. `summary`
 * is what the preview lists, so the user can see what they are about to import
 * before it is written.
 */
export const fitSessionToCandidate = (
  sessionMesg,
  setMesgs = [],
  exerciseNames = {},
) => {
  if (!sessionMesg) return null;

  const startMs = new Date(
    sessionMesg.startTime ?? sessionMesg.timestamp,
  ).getTime();
  if (!Number.isFinite(startMs)) return null;

  const seconds =
    toFiniteNumber(sessionMesg.totalTimerTime) ??
    toFiniteNumber(sessionMesg.totalElapsedTime);
  // Round UP so a 40-second mobility session is not recorded as zero minutes
  // and rejected by the builders as "no duration".
  const minutes = seconds != null ? Math.max(1, Math.ceil(seconds / 60)) : null;

  const sport = String(sessionMesg.sport || "");
  const activityType = SPORT_TO_ACTIVITY[sport] || "other";
  const label = sessionLabel(sport, sessionMesg.subSport);
  const externalId = fitExternalId(sessionMesg);
  const startedAt = new Date(startMs);

  const plan = setsToPlan(setMesgs, exerciseNames, label);

  const built = plan
    ? buildLoggedSessionFromPlan({
        plan,
        title: label,
        startedAt,
        durationMinutes: minutes,
        source: "garmin",
        externalId,
      })
    : buildLoggedSession({
        activityType,
        title: label,
        startedAt,
        durationMinutes: minutes,
        distanceKm:
          toFiniteNumber(sessionMesg.totalDistance) != null
            ? sessionMesg.totalDistance / 1000
            : null,
        avgHr: toFiniteNumber(sessionMesg.avgHeartRate),
        calories: toFiniteNumber(sessionMesg.totalCalories),
        source: "garmin",
        externalId,
      });

  if (built.error) return null;

  return {
    payload: built.payload,
    summary: {
      label,
      startedAt,
      durationMinutes: minutes,
      exerciseCount: plan ? plan.children.length : 0,
      externalId,
    },
  };
};

/**
 * Every importable activity in one decoded FIT file, newest first.
 *
 * A FIT file usually holds one session, but a multisport activity holds several
 * and a bulk export can hand back anything. `set` messages are assigned to the
 * session whose time range contains them, so a triathlon's strength block — or
 * more realistically, a file with one session — never picks up another's sets.
 */
export const buildImportCandidates = (decoded, exerciseNames = {}) => {
  const sessions = decoded?.sessionMesgs || [];
  const sets = decoded?.setMesgs || [];
  if (sessions.length === 0) return [];

  const inSession = (mesg, session) => {
    const t = new Date(mesg?.startTime ?? mesg?.timestamp).getTime();
    if (!Number.isFinite(t)) return false;
    const start = new Date(session.startTime ?? session.timestamp).getTime();
    const seconds =
      toFiniteNumber(session.totalElapsedTime) ??
      toFiniteNumber(session.totalTimerTime) ??
      0;
    return t >= start && t <= start + seconds * 1000 + 1000;
  };

  const candidates = [];
  for (const session of sessions) {
    // With a single session, take every set rather than trusting the clock —
    // a device whose set timestamps sit fractionally outside the session window
    // would otherwise silently drop the entire lift.
    const own =
      sessions.length === 1 ? sets : sets.filter((m) => inSession(m, session));
    const candidate = fitSessionToCandidate(session, own, exerciseNames);
    if (candidate) candidates.push(candidate);
  }

  candidates.sort(
    (a, b) => b.summary.startedAt.getTime() - a.summary.startedAt.getTime(),
  );
  return candidates;
};

/** Drop candidates whose activity is already in history, by external id. */
export const dedupeAgainstHistory = (candidates, sessions = []) => {
  const seen = new Set(
    (Array.isArray(sessions) ? sessions : [])
      .filter((s) => s?.external_id)
      .map((s) => `${s.source}:${s.external_id}`),
  );
  return (candidates || []).map((c) => ({
    ...c,
    alreadyImported: seen.has(`garmin:${c.summary.externalId}`),
  }));
};
